import { useVideo } from '@/context/VideoContext'
import { useEffect, useRef, useState } from 'react'
import './chapterProgress.css'

export default function ChapterProgress() {
  const { isPlaying, position, duration } = useVideo()
  const [enableTransition, setEnableTransition] = useState(true)
  const [showBar, setShowBar] = useState(true)
  const prevDurationRef = useRef(duration)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Detect chapter changes (duration changes) and disable transition temporarily
  useEffect(() => {
    if (duration !== prevDurationRef.current && duration > 0) {
      // Duration changed to a positive value - new chapter loaded
      setEnableTransition(false)
      setShowBar(false) // Hide bar during chapter change
      prevDurationRef.current = duration

      // Clear any existing timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current)
      }

      // Re-show the bar after video has reset position
      showTimeoutRef.current = setTimeout(() => {
        setShowBar(true)
        showTimeoutRef.current = null
      }, 300)

      // Re-enable transition after bar is shown
      timeoutRef.current = setTimeout(() => {
        setEnableTransition(true)
        timeoutRef.current = null
      }, 400)

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
        if (showTimeoutRef.current) {
          clearTimeout(showTimeoutRef.current)
          showTimeoutRef.current = null
        }
      }
    }

    // Update ref for duration changes (including reset to 0)
    if (duration !== prevDurationRef.current) {
      prevDurationRef.current = duration
    }
  }, [duration])

  // Calculate percentage directly from position without smoothing animation
  // The CSS transition will provide smooth visual updates
  const percentageComplete = duration === 0 ? 0 : Math.min((position / duration) * 100, 100)

  if (!isPlaying || !showBar) {
    return null
  }

  return (
    <div>
      {/* Progress bar */}
      <span className={'sr-only'}>Progress</span>
      <div className={'progress-bar'}>
        <div
          className={`progress-fill ${enableTransition ? '' : 'no-transition'}`}
          style={{ width: `${percentageComplete}%` }}
        />
      </div>
    </div>
  )
}

export { ChapterProgress }
